import type { AdminAccessState, ViewerManifest, ViewerManifestStatus } from '../types/viewerDeployment';
import type { ViewerRouteContext } from './viewerRouteContext';
import { posthog } from '../lib/posthog';

export interface ViewerRuntimeAccess {
  adminAccessState: AdminAccessState;
  isAdminView: boolean;
  canLoadAdminData: boolean;
  shouldMountAdminDeploymentRuntime: boolean;
  showAdminRuntimeUi: boolean;
  shouldUseServerBackedDrafts: boolean;
}

export function resolveAdminAccessState(
  route: ViewerRouteContext,
  manifestStatus: ViewerManifestStatus,
  manifest: ViewerManifest | null,
): AdminAccessState {
  if (route.kind !== 'admin') {
    posthog.capture('viewer_admin_access_denied', {
      kind: route.kind,
      manifest_status: manifestStatus,
      has_manifest: !!manifest,
      viewer_mode: manifest?.viewerMode,
    });
    return 'denied';
  }

  if (manifestStatus === 'loading') {
    return 'unknown';
  }

  if (manifestStatus !== 'ready' || !manifest) {
    posthog.capture('viewer_admin_access_denied', {
      kind: route.kind,
      manifest_status: manifestStatus,
      has_manifest: !!manifest,
      viewer_mode: manifest?.viewerMode,
    });
    return 'denied';
  }

  const result = manifest.viewerMode === 'admin' && manifest.routeKind === 'admin' ? 'allowed' : 'denied';
  if (result === 'denied') {
    posthog.capture('viewer_admin_access_denied', {
      kind: route.kind,
      manifest_status: manifestStatus,
      has_manifest: !!manifest,
      viewer_mode: manifest?.viewerMode,
    });
  }
  return result;
}

export function resolveIsAdminView(params: {
  route: ViewerRouteContext;
  adminAccessState: AdminAccessState;
  manifest: ViewerManifest | null;
  legacyAdminQueryEnabled: boolean;
  legacyEnvAdminEnabled: boolean;
}): boolean {
  const { route, adminAccessState, manifest, legacyAdminQueryEnabled, legacyEnvAdminEnabled } = params;

  if (route.kind === 'unknown') {
    return legacyAdminQueryEnabled || legacyEnvAdminEnabled;
  }

  return route.kind === 'admin' && adminAccessState === 'allowed' && manifest?.viewerMode === 'admin';
}

export function resolveViewerRuntimeAccess(params: {
  route: ViewerRouteContext;
  manifestStatus: ViewerManifestStatus;
  manifest: ViewerManifest | null;
  legacyAdminQueryEnabled: boolean;
  legacyEnvAdminEnabled: boolean;
}): ViewerRuntimeAccess {
  const adminAccessState = resolveAdminAccessState(
    params.route,
    params.manifestStatus,
    params.manifest,
  );
  const isAdminView = resolveIsAdminView({
    route: params.route,
    adminAccessState,
    manifest: params.manifest,
    legacyAdminQueryEnabled: params.legacyAdminQueryEnabled,
    legacyEnvAdminEnabled: params.legacyEnvAdminEnabled,
  });
  const canLoadAdminData =
    params.route.kind === 'unknown'
      ? isAdminView
      : params.route.kind === 'admin' &&
        adminAccessState === 'allowed' &&
        params.manifest?.viewerMode === 'admin';

  const shouldMountAdminDeploymentRuntime =
    params.route.kind === 'admin' &&
    canLoadAdminData &&
    Boolean(params.route.companyId) &&
    Boolean(params.route.productId);

  const showAdminRuntimeUi =
    params.route.kind === 'unknown' ? isAdminView : shouldMountAdminDeploymentRuntime;

  return {
    adminAccessState,
    isAdminView,
    canLoadAdminData,
    shouldMountAdminDeploymentRuntime,
    showAdminRuntimeUi,
    // Server-backed drafts only make sense for canonical admin routes with
    // required identifiers, and mirrors the previous viewer-shell logic.
    shouldUseServerBackedDrafts:
      shouldMountAdminDeploymentRuntime &&
      Boolean(params.route.companyId) &&
      Boolean(params.route.productId),
  };
}
